"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { cn } from "@workspace/ui/lib/utils";
import { homeAssets } from "../../_utils/home-assets";

const HORMONE_CHART_FRAMES = homeAssets.hormoneCharts.frames;
const FIRST_HORMONE_CHART_FRAME = HORMONE_CHART_FRAMES[0]!;

const FRAME_DELAY_MS = 3000;

type HormoneChartsVisualProps = {
  imageAlt: string;
};

export function HormoneChartsVisual({ imageAlt }: HormoneChartsVisualProps) {
  const [frameIndex, setFrameIndex] = useState(0);
  const prevFrameRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setFrameIndex((cur) => {
        prevFrameRef.current = cur;
        return (cur + 1) % HORMONE_CHART_FRAMES.length;
      });
    }, FRAME_DELAY_MS);
  }, []);

  useEffect(() => {
    startInterval();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startInterval]);

  const goToFrame = useCallback(
    (index: number) => {
      prevFrameRef.current = frameIndex;
      setFrameIndex(index);
      startInterval();
    },
    [frameIndex, startInterval],
  );

  const prevIndex = prevFrameRef.current;

  return (
    <div className="mx-auto w-full max-w-[600px] overflow-hidden rounded-2xl bg-base-2 lg:max-w-[680px] xl:max-w-none xl:rounded-[25px]">
      <div
        className="relative w-full"
        style={{
          aspectRatio: `${FIRST_HORMONE_CHART_FRAME.width} / ${FIRST_HORMONE_CHART_FRAME.height}`,
        }}
      >
        {HORMONE_CHART_FRAMES.map((frame, index) => {
          const isActive = index === frameIndex;
          const isPrev = index === prevIndex && prevIndex !== frameIndex;
          return (
            <Image
              key={frame.src}
              src={frame.src}
              alt={isActive ? imageAlt : ""}
              aria-hidden={isActive ? undefined : true}
              fill
              sizes="(max-width: 1279px) min(100vw - 48px, 680px), 600px"
              className={cn(
                "object-contain transition-opacity duration-700 ease-in-out motion-reduce:transition-none motion-reduce:duration-0",
                isActive && "z-[3] opacity-100",
                isPrev && "z-[2] opacity-100",
                !isActive && !isPrev && "pointer-events-none z-[1] opacity-0",
              )}
              priority={index === 0}
            />
          );
        })}

        <div className="absolute left-[12%] lg:top-6 top-3 z-[4] flex h-[8%] w-[76%]">
          {HORMONE_CHART_FRAMES.map((_, index) => (
            <button
              key={index}
              onClick={() => goToFrame(index)}
              aria-label={`Show cycle ${index + 4}`}
              className="h-full flex-1 cursor-pointer"
            />
          ))}
        </div>
      </div>
    </div>
  );
}
